import type { EditorLayerKeyframe, EditorOverlayLayer } from '@/types/editor';
import { EDITOR_TIMELINE_FPS, snapTimeToFrame } from './frameGrid';

export type LayerAnimatableProperty = 'x' | 'y' | 'size' | 'widthPercent' | 'rotation' | 'opacity';

// NaN-safe: Math.min/max propagate NaN, so an unguarded clamp would leak "NaN"
// into layerVisualSnapshot → the ffmpeg overlay-position expression → a broken
// export. Non-finite input collapses to `min` (matches the render-worker clamp).
const clamp = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;
const finite = (value: unknown, fallback: number) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

function keyframeVisual(frame: EditorLayerKeyframe) {
  return {
    x: frame.x,
    y: frame.y,
    size: frame.size,
    widthPercent: frame.widthPercent,
    rotation: frame.rotation,
    opacity: frame.opacity,
  };
}

export function layerVisualSnapshot(layer: EditorOverlayLayer): Omit<EditorLayerKeyframe, 'id' | 'time'> {
  return {
    x: clamp(layer.x, 0, 100),
    y: clamp(layer.y, 0, 100),
    size: clamp(layer.size, 1, 1200),
    widthPercent: clamp(layer.widthPercent ?? 26, 1, 200),
    rotation: clamp(layer.rotation ?? 0, -3600, 3600),
    opacity: clamp(layer.opacity, 0, 1),
  };
}

export function cleanLayerKeyframes(value: unknown, layer: EditorOverlayLayer): EditorLayerKeyframe[] {
  if (!Array.isArray(value)) return [];
  const fallback = layerVisualSnapshot(layer);
  const byFrame = new Map<number, EditorLayerKeyframe>();
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const source = raw as Partial<EditorLayerKeyframe>;
    const time = clamp(
      snapTimeToFrame(finite(source.time, layer.start)),
      Math.max(0, layer.start),
      Math.max(layer.start, layer.end)
    );
    const frameKey = Math.round(time * EDITOR_TIMELINE_FPS);
    byFrame.set(frameKey, {
      id: typeof source.id === 'string' && source.id ? source.id : `kf-${frameKey}`,
      time,
      x: clamp(finite(source.x, fallback.x), 0, 100),
      y: clamp(finite(source.y, fallback.y), 0, 100),
      size: clamp(finite(source.size, fallback.size), 1, 1200),
      widthPercent: clamp(finite(source.widthPercent, fallback.widthPercent), 1, 200),
      rotation: clamp(finite(source.rotation, fallback.rotation), -3600, 3600),
      opacity: clamp(finite(source.opacity, fallback.opacity), 0, 1),
    });
  }
  return [...byFrame.values()].sort((left, right) => left.time - right.time).slice(0, 200);
}

export function layerVisualAt(layer: EditorOverlayLayer, time: number): EditorOverlayLayer {
  const frames = cleanLayerKeyframes(layer.keyframes, layer);
  if (frames.length === 0) return layer;
  if (time <= frames[0].time) return { ...layer, ...keyframeVisual(frames[0]), keyframes: frames };
  const last = frames[frames.length - 1];
  if (time >= last.time) return { ...layer, ...keyframeVisual(last), keyframes: frames };
  const rightIndex = frames.findIndex((frame) => frame.time >= time);
  const right = frames[rightIndex];
  const left = frames[rightIndex - 1];
  const progress = clamp((time - left.time) / Math.max(1e-6, right.time - left.time), 0, 1);
  const lerp = (from: number, to: number) => from + (to - from) * progress;
  return {
    ...layer,
    x: lerp(left.x, right.x),
    y: lerp(left.y, right.y),
    size: lerp(left.size, right.size),
    widthPercent: lerp(left.widthPercent, right.widthPercent),
    rotation: lerp(left.rotation, right.rotation),
    opacity: lerp(left.opacity, right.opacity),
    keyframes: frames,
  };
}

/** FFmpeg expression using segment-local `t`, while keyframe times remain absolute. */
export function layerKeyframeExpression(
  layer: EditorOverlayLayer,
  property: LayerAnimatableProperty,
  timelineStart: number,
  timeVariable = 't'
): string {
  const frames = cleanLayerKeyframes(layer.keyframes, layer);
  if (frames.length === 0) return String(layerVisualSnapshot(layer)[property]);
  const local = frames.map((frame) => ({ time: frame.time - timelineStart, value: frame[property] }));
  if (local.length === 1) return local[0].value.toFixed(6);
  let expression = local[local.length - 1].value.toFixed(6);
  for (let index = local.length - 2; index >= 0; index -= 1) {
    const left = local[index];
    const right = local[index + 1];
    const span = Math.max(1 / 120, right.time - left.time);
    const interpolation =
      `${left.value.toFixed(6)}+(${timeVariable}-${left.time.toFixed(6)})/${span.toFixed(6)}*` +
      `${(right.value - left.value).toFixed(6)}`;
    expression = `if(lt(${timeVariable}\\,${right.time.toFixed(6)})\\,${interpolation}\\,${expression})`;
  }
  const first = local[0];
  return `if(lte(${timeVariable}\\,${first.time.toFixed(6)})\\,${first.value.toFixed(6)}\\,${expression})`;
}
